import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { JsonEditor } from 'json-edit-react'
import { Button, Toggle, TopProgress, Typography } from '../../../components'
import { staffObjectStorageService } from '../../../api'
import { LifecycleRule } from '../../../api/staffObjectStorageService'
import { useUi } from '../../../context/ui.context'
import { Card, CardTitle, Content, FormActions, JsonEditorWrapper, MutedText } from './styles'
import { getObjectStorageError } from './utils'

type Props = { bucketId: string }

export const BucketPolicy: FC<Props> = ({ bucketId }) => {
  const [t] = useTranslation()
  const { viewAlert } = useUi()
  const [policy, setPolicy] = useState<any>({})
  const [enabled, setEnabled] = useState(false)
  const [pageLoading, setPageLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)

  const loadPolicy = () => {
    setPageLoading(true)
    setLoadFailed(false)
    staffObjectStorageService.getPolicy(bucketId)
      .then(({ data }) => {
        setEnabled(data.Policy !== null)
        setPolicy(data.Policy || {})
      })
      .catch((error) => {
        setLoadFailed(true)
        viewAlert({
          severity: 'error',
          message: getObjectStorageError(error, t('ERROR_OCCURRED')),
        })
      })
      .finally(() => setPageLoading(false))
  }

  useEffect(() => {
    loadPolicy()
  }, [bucketId])

  const save = async () => {
    try {
      setLoading(true)
      await staffObjectStorageService.updatePolicy(bucketId, enabled ? policy : null)
      viewAlert({ severity: 'information', message: t('BUCKET_POLICY_UPDATED') })
    } catch (error) {
      viewAlert({
        severity: 'error',
        message: getObjectStorageError(error, t('ERROR_OCCURRED')),
      })
    } finally {
      setLoading(false)
    }
  }

  if (pageLoading) return <TopProgress loading />
  if (loadFailed) {
    return (
      <Content>
        <Card>
          <CardTitle>{t('BUCKET_POLICY')}</CardTitle>
          <MutedText>{t('OBJECT_STORAGE_CONFIGURATION_LOAD_FAILED')}</MutedText>
          <FormActions>
            <Button variant="stroke" size="display" title={t('RETRY')} onClick={loadPolicy} />
          </FormActions>
        </Card>
      </Content>
    )
  }

  return (
    <Content>
      <Card>
        <CardTitle>{t('BUCKET_POLICY')}</CardTitle>
        <MutedText>{t('STAFF_BUCKET_POLICY_DESCRIPTION')}</MutedText>
        <Toggle
          title={t('POLICY_ENABLED')}
          value={enabled}
          onChange={() => setEnabled((value) => !value)}
        />
        {enabled ? (
          <JsonEditorWrapper>
            <JsonEditor data={policy} setData={setPolicy} />
          </JsonEditorWrapper>
        ) : (
          <Typography variant="body">{t('NO_BUCKET_POLICY')}</Typography>
        )}
        <FormActions>
          <Button
            variant="primary"
            size="display"
            title={t('SAVE')}
            loading={loading}
            onClick={save}
          />
        </FormActions>
      </Card>
    </Content>
  )
}

export const BucketLifecycle: FC<Props> = ({ bucketId }) => {
  const [t] = useTranslation()
  const { viewAlert } = useUi()
  const [rules, setRules] = useState<any[]>([])
  const [pageLoading, setPageLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)

  const loadLifecycle = () => {
    setPageLoading(true)
    setLoadFailed(false)
    staffObjectStorageService.getLifecycle(bucketId)
      .then(({ data }) => {
        const responseRules = (data as { Rules?: LifecycleRule[] | null }).Rules
        setRules(Array.isArray(responseRules) ? responseRules : [])
      })
      .catch((error) => {
        setLoadFailed(true)
        viewAlert({
          severity: 'error',
          message: getObjectStorageError(error, t('ERROR_OCCURRED')),
        })
      })
      .finally(() => setPageLoading(false))
  }

  useEffect(() => {
    loadLifecycle()
  }, [bucketId])

  const save = async () => {
    try {
      setLoading(true)
      await staffObjectStorageService.updateLifecycle(bucketId, rules as LifecycleRule[])
      viewAlert({ severity: 'information', message: t('BUCKET_LIFECYCLE_UPDATED') })
    } catch (error) {
      viewAlert({
        severity: 'error',
        message: getObjectStorageError(error, t('ERROR_OCCURRED')),
      })
    } finally {
      setLoading(false)
    }
  }

  if (pageLoading) return <TopProgress loading />
  if (loadFailed) {
    return (
      <Content>
        <Card>
          <CardTitle>{t('LIFECYCLE_RULES')}</CardTitle>
          <MutedText>{t('OBJECT_STORAGE_CONFIGURATION_LOAD_FAILED')}</MutedText>
          <FormActions>
            <Button variant="stroke" size="display" title={t('RETRY')} onClick={loadLifecycle} />
          </FormActions>
        </Card>
      </Content>
    )
  }

  return (
    <Content>
      <Card>
        <CardTitle>{t('LIFECYCLE_RULES')}</CardTitle>
        <MutedText>{t('STAFF_BUCKET_LIFECYCLE_DESCRIPTION')}</MutedText>
        <JsonEditorWrapper>
          <JsonEditor data={rules} setData={(data) => setRules(data as any[])} />
        </JsonEditorWrapper>
        <FormActions>
          <Button
            variant="primary"
            size="display"
            title={t('SAVE')}
            loading={loading}
            onClick={save}
          />
        </FormActions>
      </Card>
    </Content>
  )
}
